import { useState } from 'react';

import { Markdown } from '@components/markdown/Markdown';
import { NewsletterApiForm } from '@components/newsletterApiForm/NewsletterApiForm';

import { handleError } from '@common/utils/handleError';
import { getMailchimpUrl } from '@common/utils/mailchimp';
import { isGoogleAnalyticsAvailable } from '@common/utils/tracking';
import {
	ApiSubscribeErrorResponse,
	ApiSubscribeSuccessResponse,
	NewsletterPopUpRecord,
	NewsletterSignUpLocation,
} from '@interfaces';

import styles from './newsletter-pop-up-content.module.scss';

interface NewsletterPopUpContentProps {
	popUp: NewsletterPopUpRecord;
	location: NewsletterSignUpLocation;
	storageKey?: string;
}

export const NewsletterPopUpContent = ({
	popUp: { title, form, text },
	location,
	storageKey,
}: NewsletterPopUpContentProps) => {
	const [isSuccesfullySubmitted, setIsSuccesfullySubmitted] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const {
		thankYouMessage,
		mailchimpFormLocationFieldTag,
		mailchimpListId,
		emailInputLabel,
	} = form;

	const onSuccess = ({ status, email }: ApiSubscribeSuccessResponse) => {
		if (status === 'subscribed') {
			setErrorMessage(null);
			setIsSuccesfullySubmitted(true);

			if (storageKey) {
				localStorage.setItem(storageKey, 'subscribed');
			}

			if (isGoogleAnalyticsAvailable()) {
				ga('send', 'event', {
					eventAction: 'submit pop-up',
					eventCategory: 'Outbound Form',
					eventLabel: `${form.mailchimpListId} / ${location}`,
				});
			}
		} else {
			setIsSuccesfullySubmitted(false);

			setErrorMessage(
				`We can’t subscribe you, please try our [Mailchimp form](${getMailchimpUrl(
					{
						mailchimpListId,
						email,
					},
				)})`,
			);
		}
	};

	const onError = ({ error }: ApiSubscribeErrorResponse) => {
		handleError(error);
		setErrorMessage('We’re sorry, something went wrong...');
	};

	return (
		<div className={styles.content}>
			<h2 className={styles.title}>{title}</h2>
			<Markdown
				className={styles.text}
				text={isSuccesfullySubmitted ? thankYouMessage.text : text}
			/>
			{!isSuccesfullySubmitted ? (
				<NewsletterApiForm
					emailInputLabel={emailInputLabel}
					listId={mailchimpListId}
					location={location}
					locationFieldTag={mailchimpFormLocationFieldTag}
					onError={onError}
					onSuccess={onSuccess}
				/>
			) : null}
			{errorMessage ? <Markdown text={errorMessage} /> : null}
		</div>
	);
};
